import * as React from 'react';
import { type VariantProps } from 'class-variance-authority';
import { cn } from '../../utils/cn';
import { XStrokeIcon } from '../../icons';
import { InfoTooltip } from '../Tooltip/InfoTooltip';
import { Popover, PopoverContent, PopoverTrigger, PopoverAnchor } from '../../ui/shadcn/popover';
import { SelectMenu } from '../Select/SelectMenu';
import {
  inputContainerVariants,
  inputLabelVariants,
  inputOptionalTextVariants,
  inputFieldContainerVariants,
  inputIconWrapperVariants,
  inputTextFieldVariants,
  inputClearButtonVariants,
  inputHintTextVariants,
  inputInfoIconVariants,
  inputDropdownButtonVariants,
  INPUT_TYPE,
  INPUT_STATE,
  type InputType,
  type InputState
} from './styles';

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'>,
    Pick<VariantProps<typeof inputContainerVariants>, 'fullWidth'> {
  /**
   * Input type variant
   */
  inputType?: InputType;
  /**
   * Input state (controlled externally)
   */
  state?: InputState;
  /**
   * HTML input type
   */
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url' | 'search';
  /**
   * Label text
   */
  label?: string;
  /**
   * Whether to show label
   */
  showLabel?: boolean;
  /**
   * Optional text next to label
   */
  optionalText?: string;
  /**
   * Whether to show info icon next to label
   */
  showInfoIcon?: boolean;
  /**
   * Info icon tooltip content
   */
  infoIconTooltip?: string;
  /**
   * Hint/help text below input
   */
  hintText?: string;
  /**
   * Whether to show hint text
   */
  showHintText?: boolean;
  /**
   * Error state
   */
  error?: boolean;
  /**
   * Leading icon (left side)
   */
  leadingIcon?: React.ReactNode;
  /**
   * Trailing dropdown button text (for trailing-dropdown type)
   */
  dropdownButtonText?: string;
  /**
   * Trailing dropdown icon (right side icon in button)
   */
  dropdownIcon?: React.ReactNode;
  /**
   * Dropdown menu content (SelectOption elements, for trailing-dropdown type)
   */
  dropdownChildren?: React.ReactNode;
  /**
   * Controlled dropdown open state
   */
  dropdownOpen?: boolean;
  /**
   * Callback when dropdown open state changes
   */
  onDropdownOpenChange?: (open: boolean) => void;
  /**
   * Whether to show clear (X) button
   */
  showClearButton?: boolean;
  /**
   * Clear button click handler
   */
  onClear?: () => void;
  /**
   * Value change callback (receives string value directly)
   */
  onValueChange?: (value: string) => void;
  /**
   * Dropdown indicator click handler (for trailing-dropdown type)
   */
  onDropdownClick?: () => void;
  /**
   * Container class name
   */
  containerClassName?: string;
  /**
   * ID for testing
   */
  dataTestId?: string;
}

/**
 * Input component - Text input field with multiple variants and states
 *
 * Based on Figma Design System v1.0.0
 * Supports: default, leading-icon, trailing-dropdown types
 * States: default, filled, focused, filled-focused, disabled
 */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      inputType = INPUT_TYPE.DEFAULT,
      state: controlledState,
      type = 'text',
      label,
      showLabel = true,
      optionalText,
      showInfoIcon = false,
      infoIconTooltip,
      hintText,
      showHintText = true,
      error = false,
      leadingIcon,
      dropdownButtonText,
      dropdownIcon,
      dropdownChildren,
      dropdownOpen: controlledDropdownOpen,
      onDropdownOpenChange,
      showClearButton = false,
      onClear,
      onValueChange,
      onDropdownClick,
      disabled = false,
      value,
      onChange,
      onFocus,
      onBlur,
      fullWidth = true,
      containerClassName,
      className,
      dataTestId = 'input',
      ...props
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = React.useState(false);
    const [hasValue, setHasValue] = React.useState(!!value);
    const [internalDropdownOpen, setInternalDropdownOpen] = React.useState(false);

    const isDropdownControlled = controlledDropdownOpen !== undefined;
    const dropdownOpen = isDropdownControlled ? controlledDropdownOpen : internalDropdownOpen;

    const handleDropdownOpenChange = (open: boolean) => {
      if (!isDropdownControlled) {
        setInternalDropdownOpen(open);
      }
      onDropdownOpenChange?.(open);
    };

    const handleDropdownOptionClick = (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[role="option"]')) {
        handleDropdownOpenChange(false);
      }
    };

    // Determine current state
    const currentState =
      controlledState ??
      (disabled
        ? INPUT_STATE.DISABLED
        : isFocused && hasValue
          ? INPUT_STATE.FILLED_FOCUSED
          : isFocused
            ? INPUT_STATE.FOCUSED
            : hasValue
              ? INPUT_STATE.FILLED
              : INPUT_STATE.DEFAULT);

    // Handle change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setHasValue(e.target.value.length > 0);
      onChange?.(e);
      onValueChange?.(e.target.value);
    };

    // Handle focus
    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    // Handle blur
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    // Handle clear
    const handleClear = () => {
      setHasValue(false);
      onClear?.();
    };

    return (
      <div
        className={cn(inputContainerVariants({ fullWidth }), containerClassName)}
        data-testid={dataTestId}>
        {/* Label Section */}
        {showLabel && label && (
          <div className="flex items-center gap-3xs mb-3xs">
            <label className={cn(inputLabelVariants({ disabled }))}>
              {label}
              {optionalText && (
                <span className={cn(inputOptionalTextVariants(), 'ml-4xs')}>
                  ({optionalText})
                </span>
              )}
            </label>
            {showInfoIcon && (
              <InfoTooltip
                content={infoIconTooltip}
                iconClassName={cn(inputInfoIconVariants())}
                arrow="top-center"
              />
            )}
          </div>
        )}

        {/* Input Field Container */}
        {inputType === INPUT_TYPE.TRAILING_DROPDOWN ? (
          <Popover open={dropdownOpen} onOpenChange={handleDropdownOpenChange}>
            <PopoverAnchor asChild>
              <div
                className={cn(
                  inputFieldContainerVariants({ state: currentState, error })
                )}>
                {/* Input Field */}
                <input
                  ref={ref}
                  type={type}
                  value={value}
                  onChange={handleChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  disabled={disabled}
                  className={cn(inputTextFieldVariants({ state: currentState }), className)}
                  data-testid={`${dataTestId}-field`}
                  {...props}
                />

                {/* Trailing Dropdown Button */}
                {dropdownButtonText && (
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      disabled={disabled}
                      className={cn(inputDropdownButtonVariants({ disabled }))}
                      data-testid={`${dataTestId}-dropdown-button`}>
                      <span>{dropdownButtonText}</span>
                      {dropdownIcon && (
                        <span className={cn('transition-transform', dropdownOpen && 'rotate-180')}>
                          {dropdownIcon}
                        </span>
                      )}
                    </button>
                  </PopoverTrigger>
                )}

                {/* Clear Button (X button) */}
                {showClearButton && !disabled && hasValue && (
                  <button
                    type="button"
                    onClick={handleClear}
                    className={cn(inputClearButtonVariants({ disabled: false }))}
                    data-testid={`${dataTestId}-clear-button`}>
                    <XStrokeIcon />
                  </button>
                )}
              </div>
            </PopoverAnchor>
            {dropdownChildren && dropdownOpen && (
              <PopoverContent
                className="w-auto p-none border-none shadow-none bg-transparent"
                align="end"
                sideOffset={4}
                onOpenAutoFocus={(e) => e.preventDefault()}
                onCloseAutoFocus={(e) => e.preventDefault()}
              >
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                <div onClick={handleDropdownOptionClick}>
                  <SelectMenu>
                    {dropdownChildren}
                  </SelectMenu>
                </div>
              </PopoverContent>
            )}
          </Popover>
        ) : (
          <div
            className={cn(
              inputFieldContainerVariants({ state: currentState, error })
            )}>
            {/* Leading Icon */}
            {inputType === INPUT_TYPE.LEADING_ICON && leadingIcon && (
              <div className={cn(inputIconWrapperVariants({ disabled }))}>
                {leadingIcon}
              </div>
            )}

            {/* Input Field */}
            <input
              ref={ref}
              type={type}
              value={value}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              disabled={disabled}
              className={cn(inputTextFieldVariants({ state: currentState }), className)}
              data-testid={`${dataTestId}-field`}
              {...props}
            />

            {/* Clear Button (X button) */}
            {showClearButton && !disabled && hasValue && (
              <button
                type="button"
                onClick={handleClear}
                className={cn(inputClearButtonVariants({ disabled: false }))}
                data-testid={`${dataTestId}-clear-button`}>
                <XStrokeIcon />
              </button>
            )}
          </div>
        )}

        {/* Hint Text */}
        {showHintText && hintText && (
          <div className={cn(inputHintTextVariants({ error }))}>
            <span className="min-w-0">{hintText}</span>
          </div>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { INPUT_TYPE, INPUT_STATE };
